import { useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../ui/sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { Methodology, InputType } from "../../../types";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { assetTypeOptions, inputTypeOptions } from "../utils/methodologyUtils";
import { Textarea } from "../../ui/textarea";
import { BaselineApproachForm } from "./BaselineApproachForm";
import { ScrollArea } from "../../ui/scroll-area";

const methodologySchema = z.object({
  id: z.string().min(1, "ID is required"),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  domain: z.string().min(1, "Domain is required"),
  asset_type: z.enum(["land_management_unit", "fertilizer_production_facility", "generic"] as const),
  project_input_type: z.enum([
    "conventional_fertilizer",
    "nitrogen_stabilized_fertilizer",
    "controlled_release_fertilizer",
    "low_carbon_fertilizer",
    "fuel",
    "other"
  ] as const),
  yield_period: z.number().min(1, "Yield period is required"),
  yield_period_unit: z.string().min(1, "Yield period unit is required"),
  variables: z.array(z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    display_name: z.string().optional(),
    type: z.enum(["string", "number", "boolean", "select"]),
    required: z.boolean(),
    options: z.array(z.object({
      value: z.string(),
      label: z.string()
    })).optional(),
    default: z.any().optional(),
    domain: z.string().optional(),
    url: z.string().optional(),
    metadata: z.record(z.any()).optional(),
    dynamic: z.boolean().optional(),
    level: z.string().optional(),
    conditionalOn: z.object({
      requirementId: z.string(),
      value: z.any()
    }).optional(),
    notes: z.string().optional()
  })).optional(),
  constants: z.array(z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    display_name: z.string().optional(),
    value: z.number()
  })).optional(),
  equations: z.array(z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    expression: z.string(),
    group_by: z.array(z.string()).optional(),
    aggregation: z.string().optional(),
    input_var: z.string().optional(),
    notes: z.string().optional()
  })),
  baseline_approaches: z.array(z.object({
    id: z.string().optional(),
    name: z.string(),
    description: z.string().optional(),
    yield_periods_required: z.number(),
    update_frequency: z.number(),
    level: z.enum(["asset", "region"]),
    baseline_input_type: z.enum([
      "conventional_fertilizer",
      "nitrogen_stabilized_fertilizer",
      "controlled_release_fertilizer",
      "low_carbon_fertilizer",
      "fuel",
      "other"
    ] as const),
    metadata: z.record(z.any()).optional()
  })),
  metadata: z.record(z.any()).optional(),
  version: z.string().optional(),
  status: z.string().optional()
});

type MethodologyFormData = z.infer<typeof methodologySchema>;

interface MethodologySidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  methodology?: Methodology | null;
  onSave: (data: Partial<Methodology>) => void;
}

export function MethodologySidePanel({
  open,
  onOpenChange,
  methodology,
  onSave,
}: MethodologySidePanelProps) {
  const form = useForm<MethodologyFormData>({
    resolver: zodResolver(methodologySchema),
    defaultValues: {
      id: "",
      name: "",
      description: "",
      domain: "",
      asset_type: "generic" as const,
      project_input_type: "conventional_fertilizer" as const,
      yield_period: 1,
      yield_period_unit: "year",
      variables: [],
      constants: [],
      equations: [],
      baseline_approaches: [],
      metadata: {},
      version: "",
      status: ""
    },
  });

  useEffect(() => {
    if (methodology) {
      form.reset({
        id: methodology.id,
        name: methodology.name,
        description: methodology.description || "",
        domain: methodology.domain,
        asset_type: methodology.asset_type,
        project_input_type: methodology.project_input_type,
        yield_period: methodology.yield_period || 1,
        yield_period_unit: methodology.yield_period_unit || "year",
        variables: methodology.variables || [],
        constants: methodology.constants || [],
        equations: methodology.equations || [],
        baseline_approaches: methodology.baseline_approaches || [],
        metadata: methodology.metadata || {},
        version: methodology.version || "",
        status: methodology.status || ""
      });
    } else {
      form.reset({
        id: "",
        name: "",
        description: "",
        domain: "",
        asset_type: "generic" as const,
        project_input_type: "conventional_fertilizer" as const,
        yield_period: 1,
        yield_period_unit: "year",
        variables: [],
        constants: [],
        equations: [],
        baseline_approaches: [],
        metadata: {},
        version: "",
        status: ""
      });
    }
  }, [methodology, form]);

  const onSubmit = (data: MethodologyFormData) => {
    console.log('onSubmit', data);
    const transformedData = {
      ...data,
      variables: data.variables?.map(v => ({
        ...v,
        conditionalOn: v.conditionalOn ? {
          requirementId: v.conditionalOn.requirementId,
          value: v.conditionalOn.value ?? null
        } : undefined
      })),
      baseline_approaches: data.baseline_approaches?.filter(approach => 
        approach.name && approach.level && approach.yield_periods_required && approach.update_frequency
      ),
      yield_period: Number(data.yield_period),
      yield_period_unit: data.yield_period_unit
    };
    onSave(transformedData);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[800px] sm:max-w-[800px]">
        <SheetHeader className="mb-6">
          <SheetTitle>{methodology ? "Edit Methodology" : "Create Methodology"}</SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-8rem)] pr-6">
          <Form {...form}>
            <form onSubmit={(e) => {
              e.preventDefault();
              console.log('Form submit event triggered');
              form.handleSubmit(
                (data) => {
                  console.log('Form validation passed, data:', data);
                  onSubmit(data);
                },
                (errors) => {
                  console.log('Form validation failed:', errors);
                }
              )(e);
            }} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ID</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Enter methodology description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="domain"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Domain</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="asset_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Asset Type</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select asset type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {assetTypeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="project_input_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Project Input Type</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select input type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {inputTypeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="yield_period"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Yield Period</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="yield_period_unit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Unit</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <BaselineApproachForm control={form.control} name="baseline_approaches" />

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {methodology ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
} 